import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import useCustomToaster from "hooks/useCustomToaster";
import Papa from "papaparse";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { getOrders, updateBulkOrder } from "services/order.services";
import { getTemplates } from "services/template.services";
import { setRefetchBillingData } from "store/billingSlice";
import { toggleIsUploadBulkOrderUpdateModalOpen } from "store/orderSlice";

import { getPorts, getTotalSubSuppliers } from "services/sub_suppliers.services";
import { getSubTiersList } from "services/subtiers.services";
import { formattedDateOrderDateIntoObject } from "utils/helper";
import UpdateOrderPreview from "./UpdateOrderPreview";
import UpdateViewOrder from "./UpdateViewOrder";

const UpdateBulkOrder = ({
    closeModal,
    refetch,
    // seasonList,
    articleListQuery,
    artcileLoading,
    artcileStatus,
    // agentList,
    // retailerList,
    supplierList,
}) => {
    const [fileData, setFileData] = useState(null);
    const { user, currentWorkspace } = useSelector((state) => state.authenticationSlice);
    const { workspaceId } = useParams();
    const { refetchBillingData, workspacePlan } = useSelector((state) => state.billingSlice);
    const queryClient = useQueryClient();
    const [workspaceRolePrefix, setWorkspaceRolePrefix] = useState(null);

    const dispatch = useDispatch();
    const { control, setValue } = useForm();
    const { showCustomToast } = useCustomToaster();
    const [articleList, setArticleList] = useState([]);
    const [orderList, setOrderList] = useState([]);
    const [subTiers, setSubTiers] = useState([]);
    const [portList, setPortList] = useState([]);
    // const [supplierList, setSupplierList] = useState([]);
    const [subSupplierList, setSubSupplierList] = useState([]);
    // const [templateList, setTemplateList] = useState([]);
    const [isDragOver, setIsDragOver] = useState(false);
    const [preview, setPreview] = useState(false);
    const [uploadedData, setUploadedData] = useState(null);
    const [showLimitText, setShowLimitText] = useState(false);

    const {
        data: portQueryData,
        isLoading: isPortQueryDatLoading,
        // refetch: refetchPortQueryData,
        status: portQueryDataStatus,
    } = useQuery({
        queryKey: ["ports", "getPorts", workspaceId],
        queryFn: () => getPorts({ workspace_id: parseInt(workspaceId) }),
        enabled: !!workspaceId,
    });

    const {
        data: orderListQuery,
        isLoading: isOrderListLoading,
        status: orderListQueryStatus,
    } = useQuery({
        queryKey: ["orders-all", "getOrders", workspaceId],
        queryFn: () => getOrders({
            workspace_id: parseInt(workspaceId),
            pagination: "no",
        }),
        enabled: !!workspaceId,
    });

    const { data: subTiersQuery, isLoading: subTiersLoading, status: subTiersQueryStatus } = useQuery({
        queryKey: ["subTiers", "getSubTiersList"],
        queryFn: getSubTiersList,
    });

    const {
        data: subSupplierListQuery,
        isLoading: isSubSupplierListLoading,
        status: subSupplierListQueryStatus,
    } = useQuery({
        queryKey: ["subSuppliers-all", "getAllSubSuppliers", workspaceId],
        queryFn: () => getTotalSubSuppliers({
            workspace_id: parseInt(workspaceId),
            pagination: "no",
        }),
        enabled: !!workspaceId,
    });

    useEffect(() => {
        if (
            articleListQuery &&
            artcileStatus === "success" &&
            !artcileLoading &&
            articleListQuery.status === 200 &&
            articleListQuery?.data?.articles &&
            articleListQuery?.data?.articles?.length > 0
        ) {
            const formatedArticleList = articleListQuery?.data?.articles?.map((article) => ({
                label: `${article?.article_id}`,
                value: article?.article_id,
            }));
            setArticleList(formatedArticleList);
        }
    }, [articleListQuery, artcileLoading, artcileStatus]);

    useEffect(() => {
        if (orderListQuery && !isOrderListLoading && orderListQueryStatus === "success") {
            if (orderListQuery?.data && orderListQuery?.data?.orders) {
                const formatedOrderList = orderListQuery?.data?.orders?.filter((order) => order.status_name !== "draft")?.map((order) => ({
                    label: order.order_number,
                    value: order.order_number,
                    articleId: order.articleId,
                    supplierName: order.supplier_name,
                    supplierId: order.supplier_id,
                    subSupplierIds: getEmptyOrderTiersSubSuppliers(order),
                }));
                setOrderList(formatedOrderList);
            }
        }
    }, [orderListQuery, isOrderListLoading, orderListQueryStatus]);

    useEffect(() => {
        if (subSupplierListQuery && !isSubSupplierListLoading && subSupplierListQueryStatus === "success") {
            if (subSupplierListQuery?.data && subSupplierListQuery?.data?.sub_supplier) {
                const formatedSubSupplierList = subSupplierListQuery?.data?.sub_supplier?.map((subSupplier) => ({
                    label: subSupplier.name,
                    value: subSupplier.id,
                    sub_tiers: subSupplier.sub_tiers,
                }));
                setSubSupplierList(formatedSubSupplierList);
            }
        }
    }, [subSupplierListQuery, isSubSupplierListLoading, subSupplierListQueryStatus]);

    useEffect(() => {
        if (currentWorkspace) {
            const role = currentWorkspace && currentWorkspace?.role_name?.split(".")[0];
            setWorkspaceRolePrefix(role);
        }
    }, [currentWorkspace, workspaceId]);

    useEffect(() => {
        if (portQueryData && !isPortQueryDatLoading && portQueryDataStatus === "success") {
            if (portQueryData?.data && portQueryData?.data?.ports) {
                const formatedPortList = portQueryData?.data?.ports?.map((port) => ({
                    label: port.name,
                    value: port.name,
                }));
                setPortList(formatedPortList);
            }
        }
    }, [portQueryData, isPortQueryDatLoading, portQueryDataStatus]);

    // get subt tiers
    useEffect(() => {
        if (subTiersQuery && !subTiersLoading && subTiersQueryStatus === "success") {
            if (subTiersQuery?.data && subTiersQuery?.data?.sub_tiers) {
                const formatedSubTiersList = subTiersQuery?.data?.sub_tiers?.map((subTier) => ({
                    label: subTier.name,
                    value: subTier.name,
                    id: subTier.id,
                    tier: subTier.tier,
                }));
                setSubTiers(formatedSubTiersList);
            }
        }
    }, [subTiersQuery, subTiersLoading, subTiersQueryStatus]);

    const handleCancel = () => {
        closeModal();
    };

    const handleDragEnter = (e) => {
        e.preventDefault();
        setIsDragOver(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        setIsDragOver(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragOver(false);
        onFileDropOrChange(e);
    };

    const isDragEvent = (event) => {
        return "dataTransfer" in event;
    };

    const getEmptyOrderTiersSubSuppliers = (order) => {
        const subSupIds = [];
        if (order?.tier_0) {
            subSupIds.push(...order?.tier_0);
        }
        if (order?.tier_1) {
            subSupIds.push(...order?.tier_1);
        }
        if (order?.tier_2) {
            subSupIds.push(...order?.tier_2);
        }
        if (order?.tier_3) {
            subSupIds.push(...order?.tier_3);
        }
        if (order?.tier_4) {
            subSupIds.push(...order?.tier_4);
        }
        const completedSubSupIds = [];
        if (order?.tier_0_completed) {
            completedSubSupIds.push(...order?.tier_0_completed);
        }
        if (order?.tier_1_completed) {
            completedSubSupIds.push(...order?.tier_1_completed);
        }
        if (order?.tier_2_completed) {
            completedSubSupIds.push(...order?.tier_2_completed);
        }
        if (order?.tier_3_completed) {
            completedSubSupIds.push(...order?.tier_3_completed);
        }
        if (order?.tier_4_completed) {
            completedSubSupIds.push(...order?.tier_4_completed);
        }
        const subSupplierIds = subSupIds.filter((subSupId) => !completedSubSupIds.includes(subSupId));
        // console.log("subSupplierIds", order?.order_number, subSupplierIds);
        return subSupplierIds ?? [];
    };

    // Step 1: read data from CSV file
    const onFileDropOrChange = (e) => {
        let csv = null;
        if (isDragEvent(e)) {
            csv = e.dataTransfer.files[0];
        } else {
            csv = e.target.files[0];
        }
        if (csv && csv.type !== "text/csv") {
            toast.error("Please upload a CSV file");
        } else if (csv) {
            handleFileParse(csv);
        }
    };

    // Step 2: parse CSV data and map to required format
    const handleFileParse = (csv) => {
        Papa.parse(csv, {
            complete: function (results) {
                const { data } = results;
                const headers = ["Order Number", "Article Id", "Sub Tier Name", "Sub Supplier Name", "Supplier Name", "Arrival Date", "Dispatch Date", "Departure Port", "Arrival Port", "ETA", "ETD"];

                const indices = {};
                headers.forEach((header) => {
                    const index = data[0].indexOf(header);
                    if (index !== -1) {
                        indices[header] = index;
                    }
                });

                data.shift();
                const filteredData = data.filter((row) => row.length !== 1 || row[0] !== "");
                const newData = filteredData.map((row) => {
                    let item = {};
                    headers.forEach((header) => {
                        item[header] = row[indices[header]];
                    });
                    return processRow(item, indices);
                });
                setUploadedData(newData);
            },
        });
    };

    // Step 3: process each row and map to required fields
    const processRow = (item, indices) => {
        const processedItem = {};
        let errors = {};

        const orderItem = processOrderNumber(item["Order Number"]?.replace(/ /g, "-") || "");
        // console.log("orderItem", orderItem, subTiers);
        
        if(orderItem){
            processedItem.orderNumber = orderItem;
            errors.orderNumberError = !processedItem.orderNumber;
            processedItem.articleId = {label: orderItem.articleId, value: orderItem.articleId};
            errors.articleIdError = !processedItem.articleId;

            const supList = [];
            if(orderItem.supplierName){
                supList.push({label: orderItem.supplierName, value: orderItem.supplierId});
            }
            processedItem.supplierList = supList;
            processedItem.supplier = supList.length > 0 ? supList[0] : null;
            errors.supplierError = !processedItem.supplier;
            errors.subSupplierError = !processedItem.supplier;
        } else {
            errors.orderNumberError = true;
            errors.articleIdError = true;
        }

        if(orderItem?.subSupplierIds && orderItem?.subSupplierIds?.length > 0){

            const findSubTier = subTiers.find((subTier) => item["Sub Tier Name"] === subTier.label);
            const checkedSubTier = findSubTier ? orderItem?.subSupplierIds?.includes(findSubTier?.id) : null;
            processedItem.subTier = checkedSubTier;
            errors.subTierError = processedItem.supplier ? false : !processedItem.subTier;

            const findSubSupplier = subSupplierList.find((subSupplier) => orderItem.subSupplierIds.includes(subSupplier.value));
            processedItem.subSupplier = processedItem.supplier ? null : findSubSupplier;
            errors.subSupplierError = processedItem.supplier ? false : !processedItem.subSupplier;
        } else {
            errors.subTierError = true;
            errors.subSupplierError = true;
        }

        // if subSupplier is selected, supplier is not mandatory
        if (!processedItem.subSupplier && !orderItem?.supplierName) {
            processedItem.supplier = processSupplier(item["Supplier Name"]);
            errors.supplierError = !processedItem.supplier;
        }

        const isForwarder = processedItem.subTier?.label?.toLowerCase() === "forwarder";

        processedItem.arrivalDate = processOrderDate(item["Arrival Date"]);
        errors.arrivalDateError = !processedItem.arrivalDate;

        processedItem.dispatchDate = processOrderDate(item["Dispatch Date"]);
        errors.dispatchDateError = !processedItem.dispatchDate;

        if (!processedItem.dispatchDate) {
            errors.dispatchDateError = true;
        } else if (
            processedItem.arrivalDate?.startDate &&
            processedItem.dispatchDate?.startDate &&
            processedItem.dispatchDate.startDate < processedItem.arrivalDate.startDate
        ) {
            errors.dispatchDateError = true;
        } else {
            errors.dispatchDateError = false;
        }

        if(isForwarder) {
            processedItem.dispatchDate = null;
            processedItem.arrivalDate = null;
            errors.arrivalDateError = false;
            errors.dispatchDateError = false;
        }

        processedItem.departurePort = processPort(item["Departure Port"]);
        errors.departurePortError = !isForwarder ? false : !processedItem.departurePort;

        processedItem.arrivalPort = processPort(item["Arrival Port"]);
        errors.arrivalPortError = !isForwarder ? false : !processedItem.arrivalPort;

        processedItem.eta = processOrderDate(item["ETA"]);
        errors.etaError = !isForwarder ? false : !processedItem.eta;

        processedItem.etd = processOrderDate(item["ETD"]);
        errors.etdError = !isForwarder ? false : !processedItem.etd;

        processedItem.error = errors;
        return processedItem;
    };

    const processOrderNumber = (orderNumber) => {
        if (!orderNumber) return null;
        if (orderList && orderList.length > 0) {
            const matchedOrder = orderList.find(
                (item) => normalizeString(item.label) === normalizeString(orderNumber),
            );
            return matchedOrder || null;
        } else {
            return null;
        }
    };

    const processSubTier = (subTier) => {
        if (!subTier) return null;
        if (subTiers && subTiers.length > 0) {
            const matchedSubTier = subTiers.find(
                (item) => normalizeString(item.label) === normalizeString(subTier),
            );
            return matchedSubTier || null;
        } else {
            return null;
        }
    };

    const processPort = (port) => {
        if (!port) return null;
        if (portList && portList.length > 0) {
            const matchedPort = portList.find(
                (item) => normalizeString(item.label) === normalizeString(port),
            );
            return matchedPort || null;
        } else {
            return null;
        }
    };

    const processOrderDate = (orderDate) => {
        if (!orderDate) return null;
        if (orderDate) {
            const matchedDate = formattedDateOrderDateIntoObject(orderDate);
            return matchedDate || null;
        }
    };

    const normalizeString = (str) =>
        str
            .trim()
            .replace(/[\u2018\u2019\u201A\u201B\u2032\u2035]/g, "'")
            .replace(/\s+/g, " ")
            .toLowerCase();

    // const processArticle = (article) => {
    //     if (!article) return null;
    //     if (articleList && articleList.length > 0) {
    //         const matchedArticle = articleList.find(
    //             (item) => normalizeString(item.label) === normalizeString(article),
    //         );
    //         return matchedArticle || null;
    //     } else {
    //         return null;
    //     }
    // };

    const processSupplier = (supplier) => {
        if (!supplier) return null;
        if (supplierList && supplierList.length > 0) {
            const matchedSupplier = supplierList.find(
                (item) => normalizeString(item.label) === normalizeString(supplier),
            );
            return matchedSupplier || null;
        } else {
            return null;
        }
    };

    const processSubSupplier = (subSupplier) => {
        if (!subSupplier) return null;
        if (subSupplierList && subSupplierList.length > 0) {
            const matchedSubSupplier = subSupplierList.find(
                (item) => normalizeString(item.label) === normalizeString(subSupplier),
            );
            return matchedSubSupplier || null;
        } else {
            return null;
        }
    };

    const handleErrors = (index, name, hasError, errors) => ({
        ...errors,
        [`${name}Error`]: hasError,
    });

    const onChangeHandler = (index, name, value) => {
        const updatedData = [...uploadedData];
        let currentItem = {
            ...updatedData[index],
            [name]: value,
        };

        // Update error for the changed field
        let currentErrors = handleErrors(index, name, !Boolean(value), currentItem.error);

        // when order number is changed, article id should change
        if (name === "orderNumber") {
            currentItem.articleId = {value: value.articleId, label: value.articleId};
            // currentErrors = handleErrors(index, "articleId", !Boolean(currentItem.articleId), currentErrors);
            currentErrors.articleIdError = !currentItem.articleId;

            const supList = [];
            if(value.supplierName){
                supList.push({label: value.supplierName, value: value.supplierId});
            }
            currentItem.supplierList = supList;
            currentItem.supplier = supList.length > 0 ? supList[0] : null;
            currentErrors.supplierError = !currentItem.supplier;

            currentItem.subTier = null;
            currentErrors.subTierError = true;
            currentItem.subSupplier = null;

            if(currentItem.supplier || currentItem.subSupplier){
                currentErrors.subSupplierError = false;
            } else {
                currentErrors.subSupplierError = true;
            }
            setValue(`supplier${index}`, currentItem.supplier);
            setValue(`subSupplier${index}`, currentItem.subSupplier);
            setValue(`subTier${index}`, currentItem.subTier);
        }

        // Custom validation: Dispatch Date must not be earlier than Arrival Date
        if (name === "arrivalDate" || name === "dispatchDate") {
            const arrival = currentItem.arrivalDate?.startDate;
            const dispatchDate = currentItem.dispatchDate?.startDate;

            let isDispatchError = !dispatchDate;
            if (dispatchDate && arrival && dispatchDate < arrival) {
                isDispatchError = true;
            }
            currentErrors.dispatchDateError = isDispatchError;

            // if forwarder, arrival and dispatch date should be null
            if(currentItem.subTier?.label?.toLowerCase() === "forwarder") {
                currentItem.arrivalDate = null;
                currentItem.dispatchDate = null;
                currentErrors.arrivalDateError = false;
                currentErrors.dispatchDateError = false;
            }
        }

        // Custom validation: ETD Date must not be earlier than ETA date
        if (name === "eta" || name === "etd") {
            const eta = currentItem.eta?.startDate;
            const etd = currentItem.etd?.startDate;
            const isForwarder = currentItem.subTier?.label?.toLowerCase() === "forwarder";

            if (isForwarder) {
                let isEtaError = !eta;
                if (eta && etd && eta < etd) {
                    isEtaError = true;
                }
                currentErrors.etaError = isEtaError;
            }
        }

        // if name is subSupplier and value is selected, unset supplier value and error
        if (name === "subSupplier" && value) {
            currentItem.supplier = null;
            currentErrors.supplierError = false;

            setValue(`supplier${index}`, null);
        }

        // if name is supplier and value is selected, unset subSupplier value and error
        if (name === "supplier" && value) {
            currentItem.subSupplier = null;
            currentErrors.subSupplierError = false;
            setValue(`subSupplier${index}`, null);
        }

        // sub tier changed reset subSupplier
        if (name === "subTier" && value) {
            currentItem.subSupplier = null;
            currentErrors.subSupplierError = !currentItem.supplier;

            setValue(`subSupplier${index}`, null);

            // check value.value is not forwarder then reset eta and etd
            if (value.value.toLowerCase() === "forwarder") {
                currentErrors.etaError = currentItem.eta ? false : true;
                currentErrors.etdError = currentItem.etd ? false : true;
                currentErrors.departurePortError = currentItem.departurePort ? false : true;
                currentErrors.arrivalPortError = currentItem.arrivalPort ? false : true;
                currentErrors.arrivalDateError = false;
                currentErrors.dispatchDateError = false;
            } else {
                currentErrors.etaError = false;
                currentErrors.etdError = false;
                currentErrors.departurePortError = false;
                currentErrors.arrivalPortError = false;
                currentErrors.arrivalDateError = currentItem.arrivalDate ? false : true;
                currentErrors.dispatchDateError = currentItem.dispatchDate ? false : true;
            }
        }

        // console.log("currentItem", currentItem, currentErrors);

        currentItem.error = currentErrors;
        updatedData[index] = currentItem;

        setUploadedData(updatedData);
    };

    const updateBulkOrderMutation = useMutation({
        mutationKey: ["updateBulkOrder"],
        mutationFn: (data) => updateBulkOrder(data),
        onMutate: () => {
            showCustomToast({ title: "Bulk order linking...." }, "updateBulkOrderLoadingToast");
        },
        onSuccess: (res) => {
            if (res) {
                toast.dismiss("updateBulkOrderLoadingToast");
                dispatch(toggleIsUploadBulkOrderUpdateModalOpen());
                refetch();
                if (!refetchBillingData) {
                    dispatch(setRefetchBillingData(true));
                }
                showCustomToast({ title: "Bulk order linked successfully" });
                // toast.success("Bulk order uploaded successfully");
                queryClient.invalidateQueries({ queryKey: ["updateBulkOrder"] });
            }
        },
        onError: (error) => {
            showCustomToast({ title: error.response?.data?.message });
            const msgs = error
                ? error.response?.data?.errors
                    ? Object.entries(error.response.data.errors)
                        .map(([field, message]) => message)
                        .flat()
                        .join(", ")
                    : error.response?.data?.message || error.message
                : error.response?.data?.message;
            showCustomToast({ title: msgs });
        },
        onSettled: () => {
            toast.dismiss("updateBulkOrderLoadingToast");
        },
    });

    const onSubmitBulkUpload = async () => {
        const csvData = uploadedData.map((item) => {
            // Order Number,Article Id,Sub Tier Name,Sub Supplier Name,Supplier Name,Arrival Date,Dispatch Date,Departure Port,Arrival Port,ETA,ETD
            const isForwarder = item.subTier?.value?.toLowerCase() === "forwarder";
            const formatedData = {
                "Order Number": item.orderNumber ? item.orderNumber.value : "",
                "Article Id": item.articleId ? item.articleId.value : "",
                "Sub Tier Name": item.subTier ? item.subTier.value : "",
                "Sub Supplier Name": item.subSupplier ? item.subSupplier.value : "",
                "Supplier Name": item.supplier ? item.supplier.value : "",
                "Arrival Date": item.arrivalDate ? item.arrivalDate.startDate : "",
                "Dispatch Date": item.dispatchDate ? item.dispatchDate.startDate : "",
                "Departure Port": !isForwarder ? "" : item.departurePort ? item.departurePort.value : "",
                "Arrival Port": !isForwarder ? "" : item.arrivalPort ? item.arrivalPort.value : "",
                "ETA": !isForwarder ? "" : item.eta ? item.eta.startDate : "",
                "ETD": !isForwarder ? "" : item.etd ? item.etd.startDate : "",
            };

            return formatedData;
        });

        // console.log(csvData);
        
        const totalCurrentCount = parseInt(uploadedData.length);
        const limitCount = parseInt(workspacePlan?.package?.order_lines);
        if (workspacePlan && totalCurrentCount > limitCount) {
            setShowLimitText(true);
        } else {
            setShowLimitText(false);
            const csv = Papa.unparse(csvData);
            const csvBlob = new Blob([csv], { type: "text/csv" });
            const formData = new FormData();
            formData.append("import_csv", csvBlob, "data.csv");
            formData.append("workspace_id", parseInt(workspaceId));
            if (formData) {
                updateBulkOrderMutation.mutate(formData);
            }
        }
    };

    const hasAnyError = () => {
        if (uploadedData) {
            return uploadedData?.some((data) => Object?.values(data?.error)?.includes(true));
        } else {
            return true;
        }
    };
    const handleFileDropOrChange = (e) => {
        let file = null;
        if (e.dataTransfer) {
            file = e.dataTransfer.files[0];
        } else {
            file = e.target.files[0];
        }
        if (file && file.type === "text/csv") {
            const fileSize = (file.size / (1024 * 1024)).toFixed(2) + " MB"; // Calculate file size in MB
            setFileData({
                name: file.name,
                size: fileSize,
            });
            onFileDropOrChange(e); // Continue with your existing file parsing logic
        } else {
            console.error("Please upload a valid CSV file.");
        }
    };

    const handleDownload = () => {
        if (currentWorkspace) {
            const url = `/order_transparency.csv`;
            // currentWorkspace?.role_name?.includes("retailer") ||
            //     currentWorkspace?.role_name?.includes("importer") ||
            //     currentWorkspace?.role_name?.includes("brand")
            //     ? `/client_order.csv`
            //     : currentWorkspace?.role_name?.includes("agent")
            //         ? `/agent_order_file.csv`
            //         : `/supplier_order.csv`;
            const link = document.createElement("a");
            link.href = url;
            link.download = 'order_transparency.csv';
            // currentWorkspace?.role_name?.includes("retailer") ||
            //     currentWorkspace?.role_name?.includes("importer") ||
            //     currentWorkspace?.role_name?.includes("brand")
            //     ? `client_order.csv`
            //     : currentWorkspace?.role_name?.includes("agent")
            //         ? `agent_order.csv`
            // : `supplier_order.csv`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };

    return (
        <div className="">
            <div className="rounded-xl bg-white ">
                {/* min-w-[749px] */}
                <div className="grid grid-cols-1 gap-4">
                    <div className="w-full py-6">
                        {!preview && (
                            <>
                                <UpdateViewOrder
                                    isDragOver={isDragOver}
                                    handleDragEnter={handleDragEnter}
                                    handleDragLeave={handleDragLeave}
                                    handleDrop={handleDrop}
                                    onFileDropOrChange={onFileDropOrChange}
                                    setPreview={setPreview}
                                    uploadedData={uploadedData}
                                    hasAnyError={hasAnyError}
                                    handleDownload={handleDownload}
                                    closeModal={closeModal}
                                    handleCancel={handleCancel}
                                    setFileData={setFileData}
                                    fileData={fileData}
                                    onSubmit={onSubmitBulkUpload}
                                    handleFileDropOrChange={handleFileDropOrChange}
                                />
                            </>
                        )}
                        {preview && (
                            <UpdateOrderPreview
                                data={uploadedData}
                                // seasons={seasonList}
                                articles={articleList}
                                // templates={templateList}
                                // suppliers={supplierList}
                                // agents={agentList}
                                // retailers={retailerList}
                                onChangeHandler={onChangeHandler}
                                onSubmit={onSubmitBulkUpload}
                                onSubmitBulkUpload={onSubmitBulkUpload}
                                control={control}
                                setValue={setValue}
                                hasAnyError={hasAnyError}
                                showLimitText={showLimitText}
                                closeModal={closeModal}
                                handleCancel={handleCancel}
                                preview={preview}
                                setPreview={setPreview}
                                uploadedData={uploadedData}
                                orderList={orderList}
                                subSupplierList={subSupplierList}
                                subTiers={subTiers}
                                portList={portList}
                            />
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UpdateBulkOrder;
